import { type ReactNode } from "react";
import PennText from "@/components/ui/text/PennText";
import { useCloseOnNavigate } from "@/hooks/useCloseOnNavigate";

type PennModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  label?: string;
  maxWidth?: string;
  children: ReactNode;
  footer?: ReactNode;
  zIndex?: string;
};

export const PennModal = ({
  open,
  onClose,
  title,
  subtitle,
  label,
  maxWidth = "max-w-2xl",
  children,
  footer,
  zIndex = "z-50",
}: PennModalProps) => {
  // Browser Back closes the modal (and navigates back one entry) so its
  // backdrop can't get stuck on the next page. Route-based rather than a pushed
  // history entry — see useCloseOnNavigate for why (the old pushState approach
  // broke close-one-modal-then-open-another swaps).
  useCloseOnNavigate(open, onClose);

  if (!open) return null;

  return (
    <div className={`fixed inset-0 ${zIndex}`}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          className={`relative w-full ${maxWidth} max-h-[92vh] rounded-2xl bg-white shadow-soft border border-gray-200 overflow-hidden flex flex-col`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-gray-100">
            <div className="min-w-0">
              {label && (
                <PennText as="span" size="sm" color="slate-500">
                  {label}
                </PennText>
              )}
              <PennText as="h2" size="lg" color="slate-900" font="semibold" className={label ? "mt-0.5" : ""}>
                {title}
              </PennText>
              {subtitle && (
                <PennText as="p" size="sm" color="slate-500" className="mt-1">
                  {subtitle}
                </PennText>
              )}
            </div>
            <button
              onClick={onClose}
              className="h-9 w-9 flex-shrink-0 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto">{children}</div>

          {/* Footer */}
          {footer && <div className="px-6 py-4 border-t border-gray-100">{footer}</div>}
        </div>
      </div>
    </div>
  );
};
